<ng-container *transloco="let t">
  @let showToolbarButtons = currentUserRole | showToolbarButtons;
  <ejs-appbar colorMode="Primary">
    <!--
      Single wrapper div is the idiomatic Syncfusion AppBar pattern. The
      material theme stretches `.e-appbar > div:first-child` to `width: 100%`,
      so we wrap our three sections (left / spacer / right) inside one div
      and arrange them ourselves via flex.
    -->
    <div class="appbar-content">
      <div class="appbar-left">
        <img ngSrc="../../assets/images/goatness-software-logo.png" alt="GOAT logo" priority width="127" height="45" />
        @if (currentUserRole === RoleType.OrganizationAdmin && hasMultipleOrganizations()) {
          <button ejs-button class="org-switch-button" cssClass="e-inherit" (click)="openOrganizationSwitchDialog()">
            <span class="e-icons e-organization"></span>
            <span class="org-name">{{ activeOrganizationName() || t('Select Organization') }}</span>
          </button>
        }
      </div>

      <div class="e-appbar-spacer"></div>

      <div class="appbar-right">
        @if (showToolbarButtons) {
          <div class="appbar-actions">
            <ejs-tooltip opensOn="Hover" [content]="t('Chat')" position="BottomCenter">
              <button
                id="chat"
                ejs-button
                cssClass="e-inherit"
                class="appbar-action"
                [routerLink]="'/chat'"
                routerLinkActive="is-active"
                [attr.aria-label]="t('Chat')"
              >
                <span class="e-icons e-comment-show"></span>
                @if (unreadMessagesCount(); as count) {
                  <span class="appbar-action__badge">{{ count | shortenCounter }}</span>
                }
              </button>
            </ejs-tooltip>

            <ejs-tooltip opensOn="Hover" [content]="t('Notification')" position="BottomCenter">
              <button
                id="notification"
                ejs-dropdownbutton
                target="#listview"
                cssClass="e-inherit e-caret-hide appbar-action-popup"
                class="appbar-action"
                [attr.aria-label]="t('Notification')"
              >
                <span class="e-icons e-notes"></span>
                @if (unreadNotificationsCount(); as count) {
                  <span class="appbar-action__badge">{{ count | shortenCounter }}</span>
                }
              </button>
            </ejs-tooltip>
          </div>
        }

        <ejs-menu [items]="menuItems()" cssClass="e-inherit" (select)="executeAction($event)">
          <ng-template #template let-data>
            @if (data.properties.text === ProfileMenuItem.Icon) {
              @let roleKey = roleLabelKey();
              @let nameLabel = currentUserFullName();
              @let roleLabel = roleKey ? t(roleKey) : '';
              @let pillLabel = nameLabel && roleLabel ? nameLabel + ' — ' + roleLabel : nameLabel || roleLabel;
              <div class="user-pill" [attr.aria-label]="t('Signed in as') + ' ' + pillLabel" [attr.title]="pillLabel">
                <span class="user-pill__avatar">
                  @if (currentUserInitials(); as initials) {
                    {{ initials }}
                  } @else {
                    <span class="e-icons e-user"></span>
                  }
                </span>
                @if (currentUserRole) {
                  <span class="user-pill__info">
                    <span class="user-pill__name">{{ currentUserFullName() }}</span>
                    @if (roleKey) {
                      <span class="user-pill__role">{{ t(roleKey) }}</span>
                    }
                  </span>
                }
              </div>
            } @else {
              <div class="language-selector-container">
                <span
                  [ngClass]="{
                    selected: data.properties.id === ProfileMenuItem.Language,
                  }"
                >
                  {{ t(data.properties.text) }}
                </span>
              </div>
            }
          </ng-template>
        </ejs-menu>
      </div>
    </div>
  </ejs-appbar>

  <!--
    Notification dropdown content lives outside the appbar so it doesn't
    occupy a slot in any of the appbar flex layouts. Syncfusion's
    `target="#listview"` resolves by global ID, so position in DOM doesn't
    matter — only that the element exists somewhere. Gated on
    `showToolbarButtons` so roles without notification access don't pay
    the NotificationsListService initial-fetch cost.
  -->
  @if (showToolbarButtons) {
    <goatsports-notifications-list id="listview" />
  }

  <!-- Host for dynamic dialogs -->
  <ng-template adHost></ng-template>

  <ejs-sidebar
    #sidebar
    width="220"
    dockSize="64"
    target=".main-sidebar-content"
    [enableDock]="true"
    [type]="'Push'"
    [enableGestures]="false"
    (click)="sidebar.toggle()"
    [animate]="true"
    [enablePersistence]="true"
  >
    <div class="row">
      <section style="display: flex; flex-direction: column; justify-content: space-between">
        <div>
          @for (data of currentUserRole! | showRoleBasedNavigation: routerUrl()!; track data.id!; let i = $index) {
            @if (data.text !== 'Settings') {
              @if (sidebar.isOpen) {
                <div
                  class="sidebar-tooltip"
                  style="display: flex; align-items: center"
                  [id]="'target' + i"
                  (click)="$event.stopPropagation()"
                  [routerLink]="data.id"
                  [routerLinkActive]="['e-active']"
                  [replaceUrl]="false"
                >
                  <goatsports-icon-status [dataNav]="{ name: data.text, isSelected: data.selected }" />
                  <p>{{ t(data.text!) }}</p>
                </div>
              } @else {
                <div class="collapsed-container">
                  <ejs-tooltip
                    opensOn="Hover"
                    class="sidebar-tooltip"
                    [content]="t(data.text!)"
                    [target]="'#target' + i"
                    position="BottomRight"
                    (click)="$event.stopPropagation()"
                    [routerLink]="data.id"
                    [routerLinkActive]="['e-active']"
                    [replaceUrl]="false"
                  >
                    <goatsports-icon-status
                      [id]="'target' + i"
                      [dataNav]="{ name: data.text, isSelected: data.selected }"
                    />
                  </ejs-tooltip>
                </div>
              }
            }
          }
        </div>

        <div>
          @for (data of currentUserRole! | showRoleBasedNavigation: routerUrl()!; track data.id!; let i = $index) {
            @if (data.text === 'Settings') {
              @if (sidebar.isOpen) {
                <div
                  class="sidebar-tooltip"
                  style="display: flex; align-items: center"
                  [id]="'target' + i"
                  (click)="$event.stopPropagation()"
                  [routerLink]="data.id"
                  [routerLinkActive]="['e-active']"
                  [replaceUrl]="false"
                >
                  <goatsports-icon-status [dataNav]="{ name: data.text, isSelected: data.selected }" />
                  <p>{{ t(data.text!) }}</p>
                </div>
              } @else {
                <div class="collapsed-container">
                  <ejs-tooltip
                    opensOn="Hover"
                    class="sidebar-tooltip"
                    [content]="t(data.text!)"
                    [target]="'#target' + i"
                    position="BottomRight"
                    (click)="$event.stopPropagation()"
                    [routerLink]="data.id"
                    [routerLinkActive]="['e-active']"
                    [replaceUrl]="false"
                  >
                    <goatsports-icon-status
                      [id]="'target' + i"
                      [dataNav]="{ name: data.text, isSelected: data.selected }"
                    />
                  </ejs-tooltip>
                </div>
              }
            }
          }
        </div>
      </section>

      <div class="collapse-row">
        <span [ngClass]="sidebar.isOpen ? 'collapse-icon' : 'expand-icon'"></span>
        @if (sidebar.isOpen) {
          <span>{{ t('Collapse') }}</span>
        }
      </div>
    </div>
  </ejs-sidebar>

  <div class="main-sidebar-content">
    <div style="height: 100%">
      @if (!isErrorThrown()) {
        <router-outlet />
      } @else {
        <goatsports-error-messages />
      }
    </div>
  </div>
</ng-container>
